import { DateTime } from "luxon";
import { dayjs } from './DateTimeUtc';
import type { OpUnitType } from 'dayjs';
import { isNullOrUndefined, toTimeString } from "./HelperFunctions";
import { formatDateToUtc, parseUtcToLocal } from "./DateTimeUtc";

// Re-export new UTC utilities for convenience
export { formatDateToUtc, parseUtcToLocal, isValidDate, parseRawDateString, formatRawDateString } from './DateTimeUtc';

export const FORM_DATE_TIME_FORMAT = "YYYY-MM-DD HH:mm:ss";

export function toLocalDateTime(dateTime: string) {
  var dateTimeFromISO = DateTime.fromISO(dateTime);
  return `${dateTimeFromISO.toLocaleString(
    DateTime.DATE_SHORT
  )} ${dateTimeFromISO.toLocaleString(DateTime.TIME_WITH_SHORT_OFFSET)}`;
}

export function getAge(
  firstDate: string | Date,
  secondDate: string | Date,
  unit: OpUnitType = "year"
) {
  if (isNullOrUndefined(firstDate) || isNullOrUndefined(secondDate)) {
    return null;
  }

  const age = dayjs(new Date(firstDate)).diff(
    dayjs(new Date(secondDate)),
    unit
  );

  return age;
}

export function compareDates(a?: Date | null, b?: Date | null) {
  return (
    a &&
    b &&
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDay() === b.getDay()
  );
}

export function compareTimes(a?: Date | null, b?: Date | null) {
  return a && b && toTimeString(a) === toTimeString(b);
}

export function setDateOnDateTimeString(date: string, dateTimeString: string) {
  // Parse both UTC strings correctly
  var oldDate = parseUtcToLocal(date);
  const dateTime = parseUtcToLocal(dateTimeString);

  if (!oldDate || !dateTime) {
    return '';
  }

  var newDate = new Date(
    oldDate.getFullYear(),
    oldDate.getMonth(),
    oldDate.getDate(),
    dateTime.getHours(),
    dateTime.getMinutes(),
    dateTime.getSeconds()
  );

  return toFormDateTimeFormat(newDate);
}

export function setTimeOnDateTimeString(time: string, dateTimeString: string) {
  // Parse both UTC strings correctly
  var oldTime = parseUtcToLocal(time);
  const dateTime = parseUtcToLocal(dateTimeString);

  if (!oldTime || !dateTime) {
    return '';
  }

  var newDate = new Date(
    dateTime.getFullYear(),
    dateTime.getMonth(),
    dateTime.getDate(),
    oldTime.getHours(),
    oldTime.getMinutes(),
    oldTime.getSeconds()
  );

  return toFormDateTimeFormat(newDate);
}

export function copyDate(date: Date) {
  var newDate = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    0,
    0,
    0,
    0
  );
  return newDate;
}

export function copyTime(date: Date) {
  var newDate = new Date(
    0,
    0,
    0,
    date.getHours(),
    date.getMinutes(),
    date.getSeconds(),
    date.getMilliseconds()
  );
  return newDate;
}

export function toFormDateTimeFormat(dateTime: Date) {
  // Use UTC conversion for consistent backend format
  return formatDateToUtc(dateTime, false);
}

export function formatShortDate(value: string): string {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
}

// For dateOnly fields (e.g. FollowupDate): parses the raw "YYYY-MM-DD..." date part directly
// instead of going through a JS Date object, so the calendar date never shifts in non-UTC local
// timezones regardless of whether the backend serializes a trailing "Z"/offset (see
// project_date_filter_patterns memory — unlike formatShortDate above, which is Date-object-based
// and can shift a UTC-midnight value to the previous day for negative-offset timezones).
export function formatShortDateOnly(value?: string): string {
  if (!value) return '';
  const [year, month, day] = value.split('T')[0].split('-');
  if (!year || !month || !day) return '';
  return `${parseInt(month, 10)}/${parseInt(day, 10)}/${year}`;
}

export function formatShortDateTime(value: string): string {
  if (!value) return '';
  const utc = value.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(value) ? value : value + 'Z';
  const m = utc.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!m) return '';
  const [yr, mo, dy, hr, mn] = m.slice(1).map(Number);
  // Proxy avoids pre-1900 historical timezone anomalies (e.g. UTC-6:02:04)
  const offsetMin = new Date(2000, mo - 1, dy, hr, mn, 0).getTimezoneOffset();
  const local = new Date(Date.UTC(yr, mo - 1, dy, hr, mn, 0) - offsetMin * 60000);
  const lh = local.getUTCHours();
  const h = lh % 12 || 12;
  const mm = local.getUTCMinutes().toString().padStart(2, '0');
  const ampm = lh < 12 ? 'AM' : 'PM';
  return `${local.getUTCMonth() + 1}/${local.getUTCDate()}/${local.getUTCFullYear()} ${h}:${mm} ${ampm}`;
}

export function getDaysInMonth(month: number, year: number, maxDay?: number) {
  var date = new Date(year, month, 1);
  var days = [];
  while (date.getMonth() === month && (maxDay ? days.length < maxDay : true)) {
    days.push(new Date(date));
    date.setDate(date.getDate() + 1);
  }
  return days;
}
